/**
 * Tracks page views: sends to Google Analytics only when user has tracking consent;
 * always logs user activity for super-admin audit (no GA without consent).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConsent } from '../contexts/ConsentContext';
import { initGA, pageView } from '../utils/analytics';
import { logUserActivity } from '../services/auditLogService';

export default function AnalyticsTracker() {
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();
  const { trackingConsent } = useConsent();
  const prevPathRef = useRef('');

  useEffect(() => {
    if (!trackingConsent) return;
    initGA();
  }, [trackingConsent]);

  useEffect(() => {
    const path = location.pathname || '/';
    const title = document.title || 'Table Tennis';
    const isNewPath = path !== prevPathRef.current;
    prevPathRef.current = path;
    if (trackingConsent && isNewPath) pageView(path, title);
    if (currentUser?.uid) {
      const context = {
        userId: currentUser.uid,
        userEmail: currentUser.email || null,
        displayName: userProfile?.displayName || currentUser.displayName || currentUser.email || null,
      };
      logUserActivity(context, 'page_view', path, title, { search: location.search ?? null });
    }
  }, [location.pathname, location.search, trackingConsent, currentUser?.uid, currentUser?.email, currentUser?.displayName, userProfile?.displayName]);

  // When user first accepts tracking, send current page view once (pathname intentionally omitted to avoid double-send on every navigation).
  useEffect(() => {
    if (!trackingConsent) return;
    const path = location.pathname || '/';
    const title = document.title || 'Table Tennis';
    pageView(path, title);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when consent is granted, not on pathname change
  }, [trackingConsent]);

  return null;
}
